// index.ts — which locale is active, and where that preference is kept.
//
// Same idiom as `lib/theme.ts` (the dashboard's other user preference): localStorage, with
// "system" meaning NO stored value — so a user who never chooses follows their browser forever,
// including after they change it. No server persistence this cycle (spec, "Out of scope"), no
// URL segment: the dashboard is behind auth and has no SEO surface, unlike apps/web.
//
// React-free on purpose — `lib/*.ts` copy helpers import `Locale` from here without pulling
// React into a pure module.
import { de } from './de';
import { en, type Catalog } from './en';
import { es } from './es';
import { fr } from './fr';
import { id } from './id';
import { ja } from './ja';
import { pt } from './pt';
import { vi } from './vi';
import { zh } from './zh';

export type Locale = 'en' | 'vi' | 'es' | 'pt' | 'fr' | 'de' | 'ja' | 'id' | 'zh';
/** What the user chose. 'system' is not a locale — it resolves to one. */
export type LocalePreference = Locale | 'system';

export const LOCALES: readonly Locale[] = ['en', 'vi', 'es', 'pt', 'fr', 'de', 'ja', 'id', 'zh'];
export const LOCALE_PREFERENCES: readonly LocalePreference[] = ['system', ...LOCALES];

/** Endonyms. A language name is written in its OWN language regardless of which locale is
 *  currently active — these are deliberately NOT catalog keys, same reasoning as
 *  `apps/web/app/lib/locale.ts`'s `LOCALE_LABEL`. */
export const LOCALE_LABEL: Record<Locale, string> = {
  en: 'English',
  vi: 'Tiếng Việt',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  ja: '日本語',
  id: 'Bahasa Indonesia',
  zh: '中文',
};

export const CATALOGS: Record<Locale, Catalog> = { en, vi, es, pt, fr, de, ja, id, zh };

const STORAGE_KEY = 'embent:locale';

export function getLocalePreference(): LocalePreference {
  const stored = localStorage.getItem(STORAGE_KEY);
  return (LOCALES as readonly string[]).includes(stored ?? '') ? (stored as Locale) : 'system';
}

export function setLocalePreference(pref: LocalePreference): void {
  if (pref === 'system') localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, pref);
}

/** Prefix-matches `navigator.language` against every supported locale — generalized from a
 *  single hardcoded `vi`/`vi-VN` check into a loop over `LOCALES` so a new locale needs no
 *  change here. `LOCALES` lists `en` first, but it's skipped: `en` is the fallback below
 *  regardless, and none of the other eight codes is a prefix of another, so match order among
 *  them doesn't matter ('vi', 'vi-VN', 'VI-vn' all mean Vietnamese; everything unmatched falls
 *  back to English). */
export function browserLocale(): Locale {
  const lang = navigator.language?.toLowerCase() ?? '';
  for (const locale of LOCALES) {
    if (locale === 'en') continue;
    if (lang.startsWith(locale)) return locale;
  }
  return 'en';
}

export function resolveLocale(pref: LocalePreference): Locale {
  return pref === 'system' ? browserLocale() : pref;
}
